import {
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import tableIllustration from "resources/images/table-illustration.png";
import { groupMeetingsByDate, useCalendarDrawer } from "../../../common";
import { MediumCustomIcon } from "../../layer00";
import { EmptyStateCard } from "../../layer01";
import { CalendarAppointmentsAccordion } from "../../layer20";
import { useGetAllDetectedMeetingsQuery } from "app/containers/TeamsTab/calendarApi";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectCurrentUserInfo,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";

export const CalendarList = () => {
  const { data, isLoading } = useGetAllDetectedMeetingsQuery();
  const currentUser = useAppSelector(selectCurrentUserInfo);
  const companyUsersById = useAppSelector(selectAllCompanyUsersById);
  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);

  const calendarDrawer = useCalendarDrawer();

  const now = DateTime.now();
  const weekEnd = now.startOf("week").plus({ days: 6 });
  const weekLabel = `${now.toLocaleString({
    month: "short",
    day: "numeric",
  })} - ${weekEnd.toLocaleString({ month: "short", day: "numeric" })}`;

  const userAccountIdByEmail = Object.fromEntries(
    Object.entries({ ...sampleUsersById, ...companyUsersById }).map(
      ([id, user]) => [user.emailAddress, Number(id)]
    )
  );

  const meetingsThisWeek = (data?.detected_1on1s ?? []).filter((meeting) => {
    if (meeting.rejected) return false;
    if (meeting.participants.length !== 2) return false;
    const startTime = DateTime.fromISO(meeting.startTime);
    return startTime >= now && startTime <= weekEnd.endOf("day");
  });
  const groupedMeetings = groupMeetingsByDate(
    meetingsThisWeek,
    userAccountIdByEmail,
    currentUser?.emailAddress
  );
  return (
    <Card
      className="bg-white"
      sx={{
        border: "none",
        borderRadius: 0,
        boxShadow: "none",
        flexShrink: 0,
        paddingLeft: "2rem",
        paddingRight: "2rem",
        paddingBottom: "1rem",
      }}
    >
      <CardHeader
        disableTypography
        className="border-b border-sapphire-05"
        sx={{
          minHeight: "6.25rem",
          marginLeft: "-2rem",
          marginRight: "-2rem",
          paddingLeft: "2rem",
          paddingRight: "2rem",
        }}
        title={
          <Stack>
            <Typography
              variant="cardTitle"
              className="text-navy-100"
              sx={{
                fontSize: "1.25rem",
                fontWeight: 600,
              }}
            >
              1:1 Calendar
            </Typography>
            <Typography variant="cardSubtitle">{weekLabel}</Typography>
          </Stack>
        }
        subheader=""
        action={
          <IconButton
            sx={{
              border: "none",
              boxShadow: "none",
            }}
            onClick={() => calendarDrawer.close()}
          >
            <MediumCustomIcon
              icon="CLOSE"
              stroke="var(--team-app-icon-stroke-warm)"
            />
          </IconButton>
        }
      />
      <Collapse in={true}>
        <CardContent
          sx={{
            background: "none",
            padding: 0,
          }}
        >
          {isLoading && (
            <Stack rowGap="1rem" padding="1rem">
              <Skeleton
                variant="rounded"
                height={96}
                sx={{ borderRadius: "1rem" }}
              />
              <Skeleton
                variant="rounded"
                height={96}
                sx={{ borderRadius: "1rem" }}
              />
            </Stack>
          )}
          {!isLoading && groupedMeetings.length === 0 && (
            <EmptyStateCard
              image={tableIllustration}
              title="No upcoming 1:1s"
              body="You don't have any one on ones scheduled."
            />
          )}
          {!isLoading && groupedMeetings.length > 0 && (
            <Stack
              direction="column"
              rowGap={0}
              divider={<Divider className="border-sapphire-05" />}
            >
              {groupedMeetings.map((node) => (
                <CalendarAppointmentsAccordion {...node} key={node.id} />
              ))}
            </Stack>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};
